import { cn } from '@/lib/utils';
import FoundationAvatar from '../avatar/avatar';
import { type ComponentType } from '@/lib/types';
import type {
	ChatMutationType,
	ChatWindowSourceType,
	SourcesType,
} from '../chat-window/chat-window';
import { Link, RefreshCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { ConfigContext } from '@/config/config-context';

export type AnswerStatus = '200' | '400' | '500';

export type BubbleProps = {
	time?: string;
	user: boolean;
	loading: boolean;
	message: string;
	status: AnswerStatus;
	humanHandover?: boolean;
	sources?: SourcesType[];
};

/**
 * BubbleComponentType
 *
 * Represents the properties required to render a bubble component.
 */
export type BubbleComponentType = {
	/** The bubble component type, must be 'Bubble'. -- for componentRenderer to know which Object to render -- */
	type: typeof ComponentType.Bubble;
	/** The properties specific to the bubble component. */
	props: BubbleProps;
	/** Indicates if this bubble is the first item in the list. Optional. -- to handle Refresh & Sources on welcoming message */
	firstItem?: boolean;
	/** The index of the bubble, representing the order it was created. Optional. -- to handle Refresh & Sources on a specific item*/
	index?: number;
	/** Refreshes the conversation from Index choose by user. Optional. */
	refresh?: ChatMutationType;
	/** Dispatch function to set the chat window sources. Optional. */
	setChatWindowSources?: Dispatch<SetStateAction<ChatWindowSourceType>>;
	/** The current state of chat window sources. Optional. */
	chatWindowSources?: ChatWindowSourceType;
};

type BubbleTail = {
	colored: Record<string, string>;
	mask: Record<string, string>;
};

/**
 * BubbleLoader Component
 *
 * This component renders a bubble loader animation with three bouncing bubbles.
 * Each bubble has a delay in its animation to create a sequential bouncing effect.
 *
 * @returns {JSX.Element} A JSX element containing the bubble loader animation.
 */
const BubbleLoader = (): JSX.Element => {
	return (
		<div
			aria-hidden='true'
			className='wlcb-flex wlcb-items-center wlcb-justify-center wlcb-gap-x-1'
		>
			{Array.from({ length: 3 }).map((_, index) => {
				return (
					<span
						key={index}
						style={{ animationDelay: index !== 0 ? `${index * 220}ms` : '0ms' }}
						className='wlcb-block wlcb-size-2 wlcb-rounded-full wlcb-bg-text-hint wlcb-animate-bounce'
					/>
				);
			})}
		</div>
	);
};

const Bubble = ({
	chatWindowSources,
	refresh,
	index,
	firstItem,
	setChatWindowSources,
	props: { loading, message, time, user, status, sources },
}: Omit<BubbleComponentType, 'type'>) => {
	const config = useContext(ConfigContext);
	// Tailwind Group seemed to not work correctly in all Scenarios
	// Used onFocus, onBlur, onMouseOverm & onMouseLeave to make sure buttons are always tabable in their respective order
	const [isVisible, setIsVisible] = useState<Record<string, boolean>>({
		mouse: false,
		focused: false,
	});

	// Replace new lines with <br /> tags
	const formattedMessage = message.replace(/\n/g, '<br />');

	const errorMessage: string = user
		? 'Vi kunde inte ta emot din fråga, vänligen försök igen.'
		: 'Något gick fel på våran sida, vänligen försök igen.';

	const bubbleTail: BubbleTail = user
		? {
				colored: {
					borderLeft:
						status === '500' && !loading
							? '30px solid hsl(var(--destructive))'
							: '30px solid var(--bubble-primary)',
					borderBottomLeftRadius: '95px 50px',
					transform: 'translate(-7px, -2px)',
				},
				mask: {
					borderBottomLeftRadius: '40px 50px',
					transform: 'translate(7.9px, -2px)',
				},
			}
		: {
				colored: {
					borderRight:
						status === '500' && !loading
							? '30px solid hsl(var(--destructive))'
							: '30px solid var(--bubble-secondary)',
					borderBottomRightRadius: '95px 50px',
					transform: 'translate(7px, -2px)',
				},
				mask: {
					borderBottomRightRadius: '40px 50px',
					transform: 'translate(-7.9px, -2px)',
				},
			};

	return (
		<div
			aria-live='polite'
			id='chat-bot-content-body'
			onMouseOver={() => setIsVisible((prev) => ({ ...prev, mouse: true }))}
			onMouseLeave={() => setIsVisible((prev) => ({ ...prev, mouse: false }))}
			onFocus={() => setIsVisible((prev) => ({ ...prev, focus: true }))}
			onBlur={() => setIsVisible((prev) => ({ ...prev, focus: false }))}
			className={cn(
				'wlcb-py-space-075 wlcb-flex wlcb-items-end wlcb-justify-start wlcb-gap-x-space-050 wlcb-w-full ',
				{
					'wlcb-pr-space-250 wlcb-flex-row': !user && !firstItem,
					'wlcb-pl-space-500 wlcb-pr-space-250 wlcb-flex-row-reverse': user,
					'wlcb-pr-space-500 wlcb-flex-row': !user && firstItem,
				}
			)}
		>
			{user ? null : (
				<FoundationAvatar
					className='wlcb-relative wlcb-z-10'
					image={{
						imageSrc: config?.chatBotAvatar ?? '',
						alt: 'Chatbot Avatar',
					}}
					size='sm'
					fallback='CB'
				/>
			)}
			<div className='wlcb-flex wlcb-flex-col wlcb-items-start wlcb-justify-start wlcb-gap-y-2 wlcb-w-fit'>
				<div
					className={cn(
						'wlcb-flex wlcb-items-center wlcb-gap-x-2 wlcb-w-full',
						{
							hidden: loading,
							'wlcb-justify-between': !user,
							'wlcb-justify-end': user,
						}
					)}
				>
					{loading ? null : (
						<>
							{user ? null : (
								<p className='wlcb-text-body wlcb-text-text-hint [&>span]:wlcb-text-icon-size wlcb-space-x-space-025 wlcb-select-none wlcb-leading-[16.8px]'>
									<span>🤖</span>
									{config?.chatBotName ?? 'AIssistant'}
								</p>
							)}
							<p className='wlcb-text-caption wlcb-text-text-hint'>{time}</p>
						</>
					)}
				</div>
				<div
					className={cn(
						'wlcb-rounded-lg wlcb-w-full wlcb-p-space-075 wlcb-border-[1px] wlcb-relative',
						{
							'!wlcb-w-fit': loading,
							'wlcb-border-bubble wlcb-bg-bubble': user,
							'wlcb-border-bubble-secondary wlcb-bg-bubble-secondary': !user,
							'wlcb-border-destructive wlcb-bg-destructive':
								status === '500' && !loading,
						}
					)}
				>
					<div
						aria-hidden='true'
						className={cn(
							'wlcb-absolute wlcb-z-[1] wlcb-h-[10px] wlcb--bottom-[3px] ',
							{
								'wlcb--right-[19px] wlcb-bg-bubble': user,
								'wlcb--left-[19px] wlcb-bg-bubble-secondary': !user,
								'wlcb-bg-destructive': status === '500' && !loading,
							}
						)}
						style={bubbleTail.colored}
					/>
					<div
						aria-hidden='true'
						className={cn(
							'wlcb-absolute wlcb-z-[1] wlcb-h-[14px] wlcb-w-[30px] wlcb--bottom-1 wlcb-bg-white',
							{
								'wlcb--right-[23px] ': user,
								'wlcb--left-[23px]': !user,
							}
						)}
						style={bubbleTail.mask}
					/>
					{loading ? (
						<BubbleLoader />
					) : (
						<div
							className={cn(
								'[&>p]:wlcb-text-body wlcb-max-w-prose wlcb-text-left',
								{
									'[&>p]:wlcb-text-bubble-text-primary': user,
									'[&>p]:wlcb-text-bubble-text-secondary': !user,
									'[&>p]:wlcb-text-destructive-foreground': status === '500',
								}
							)}
						>
							{status === '500' ? (
								<p>{errorMessage}</p>
							) : user ? (
								<p>{message}</p>
							) : (
								<div
									className='[&>*]:wlcb-text-body wlcb-max-w-prose [&>p]:wlcb-text-bubble-text-secondary [&>b]:wlcb-text-bubble-text-secondary [&>p>a]:wlcb-text-[#0000cd] [&>p>a]:wlcb-underline hover:[&>p>a]:wlcb-no-underline'
									dangerouslySetInnerHTML={{ __html: formattedMessage }}
								/>
							)}
						</div>
					)}
				</div>
			</div>
			{!loading && !user && !firstItem ? (
				<div
					className={cn('wlcb-gap-y-2 wlcb-flex wlcb-flex-col wlcb-h-fit', {
						'wlcb-opacity-0': !isVisible.mouse && !isVisible.focus,
						'wlcb-opacity-100': isVisible.mouse || isVisible.focus,
					})}
				>
					{sources && sources.length > 0 ? (
						<Button
							className='wlcb-bg-bubble-secondary !wlcb-rounded-full hover:!wlcb-bg-bubble-secondary hover:!wlcb-brightness-90 !wlcb-transition-all focus-visible:!wlcb-outline-2 focus-visible:!wlcb-outline-stroke-subtle-hover'
							size='sm'
							onFocus={() => setIsVisible((prev) => ({ ...prev, focus: true }))}
							onBlur={() => setIsVisible((prev) => ({ ...prev, focus: false }))}
							style={{ filter: 'brightness(1.05)' }}
							variant='ghost'
							aria-label='Öppna Sources'
							aria-description='Öppnar Source modalen och listar alla källor där chat boten hittade sitt svar'
							aria-controls='chat-window'
							aria-expanded={chatWindowSources?.open}
							disabled={loading}
							icon={{
								iconType: (
									<Link
										aria-hidden='true'
										className={`wlcb-size-4 ${config?.theme?.primary?.color ? 'wlcb-stroke-[var(--theme-brand-primary)]' : 'wlcb-stroke-black'}`}
									/>
								),
								orientation: 'default',
							}}
							onClick={() =>
								setChatWindowSources?.({
									open: true,
									index: index ?? undefined,
								})
							}
						/>
					) : null}
					<Button
						className='wlcb-bg-bubble-secondary !wlcb-rounded-full hover:!wlcb-bg-bubble-secondary hover:!wlcb-brightness-90 focus-visible:!wlcb-outline-2 focus-visible:!wlcb-outline-stroke-subtle-hover'
						size='sm'
						style={{ filter: 'brightness(1.05)' }}
						onFocus={() => setIsVisible((prev) => ({ ...prev, focus: true }))}
						onBlur={() => setIsVisible((prev) => ({ ...prev, focus: false }))}
						variant='ghost'
						aria-label='Refresh'
						aria-description='Skickar frågan mot Chat bot och återskapar svaret.'
						aria-controls='chat-window-content'
						disabled={loading}
						icon={{
							iconType: (
								<RefreshCcw
									aria-hidden='true'
									className={`wlcb-size-4 ${config?.theme?.primary?.color ? 'wlcb-stroke-[var(--theme-brand-primary)]' : 'wlcb-stroke-black'}`}
								/>
							),
							orientation: 'default',
						}}
						onClick={() => refresh?.mutate(`${index}`)}
					/>
				</div>
			) : null}
		</div>
	);
};

export default Bubble;
